import { useEffect, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import ApartmentIcon from "@mui/icons-material/Apartment";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContactsIcon from "@mui/icons-material/Contacts";
import InsightsIcon from "@mui/icons-material/Insights";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RuleIcon from "@mui/icons-material/Rule";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import FolderIcon from "@mui/icons-material/Folder";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AddIcon from "@mui/icons-material/Add";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import LinkIcon from "@mui/icons-material/Link";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, Badge, Box, IconButton, InputAdornment, Snackbar, TextField, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useLayout } from "../contexts/LayoutContext";
import AppIconButton from "../components/common/AppIconButton";
import UniversalActionButton, {
  UNIVERSAL_BUTTON_STYLES,
} from "../components/common/UniversalActionButton";
import ContextSharePopup from "../components/common/ContextSharePopup";
import HelpLauncher from "../components/help/HelpLauncher";
import Gate from "../components/common/Gate";
import { OPEN_GLOBAL_REMINDER_EVENT, JOURNAL_ACTION_EVENT } from "../utils/constants";
import { apiGetJournals } from "../graphQL/journal";
import ReminderCreateDialog, { getReminderContextDefaults } from "../components/reminders/ReminderCreateDialog";
import "./AppBar.scss";

const CONTEXTSHARE_URL = process.env.REACT_APP_CONTEXTSHARE_URL || "https://contextshare-app.yourrighthand.ai";

const AppBar = () => {
  const { isSmall, toggleSidebar, isPanelOpen, togglePanel, setPanels, addressbookToolbar, propertiesToolbar, matchToolbar, reminderInboxOpen, toggleReminderInbox } = useLayout();
  const authorId = useSelector((state) => state.auth.userId);
  const userDomain = useSelector((state) => state.auth.user?.domain || "");
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [contextShareOpen, setContextShareOpen] = useState(false);
  const [mobileChatToastOpen, setMobileChatToastOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);

  // Count of due (triggered, not done) reminders — drives the badge on the
  // Reminders panel toggle so a firing reminder is visible from the app bar.
  const [dueReminderCount, setDueReminderCount] = useState(0);
  useEffect(() => {
    if (!authorId) return undefined;
    let cancelled = false;
    const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";
    const poll = async () => {
      try {
        const res = await apiGetJournals({
          authorId, type: "reminder", sort: "dueDate", offset: 0, limit: 50,
          ...(activeDomain ? { domain: activeDomain } : {}),
        });
        const items = res?.journals || [];
        const due = items.filter((r) => {
          const d = typeof r.data === "string" ? (() => { try { return JSON.parse(r.data); } catch { return {}; } })() : (r.data || {});
          const done = ["done", "completed"].includes(String(d?.status || "").trim().toLowerCase());
          return (r.state === "due" || r.status === "triggered") && !done;
        }).length;
        if (!cancelled) setDueReminderCount(due);
      } catch { /* badge is advisory; ignore poll errors */ }
    };
    poll();
    const interval = setInterval(poll, 60000);
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [authorId, userDomain]);

  const remindersToggleIcon = (
    <Badge
      badgeContent={dueReminderCount}
      color="error"
      overlap="circular"
      sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16, px: 0.5 } }}
    >
      <NotificationsActiveIcon fontSize="small" />
    </Badge>
  );

  const openContextShare = () => {
    setContextShareOpen(true);
  };


  const handleChatClick = () => {
    if (isMobile) {
      setMobileChatToastOpen(true);
      return;
    }
    const opening = !isPanelOpen("chat");
    if (opening) {
      setPanels({ chat: true, details: false, comments: false });
    } else {
      setPanels({ chat: false, details: true });
    }
  };

  const handleCreateGlobalReminder = () => {
    window.dispatchEvent(new CustomEvent(OPEN_GLOBAL_REMINDER_EVENT, { detail: { source: "appbar" } }));
  };

  // Standalone Journal (/journal): trigger the same JournalPanel toolbar action
  // the in-panel button would. anchorEl lets the panel anchor its filter menu.
  const dispatchJournalAction = (action) => (e) => {
    window.dispatchEvent(new CustomEvent(JOURNAL_ACTION_EVENT, {
      detail: { action, anchorEl: e?.currentTarget || null },
    }));
  };

  useEffect(() => {
    const openGlobalReminder = () => setReminderDialogOpen(true);
    window.addEventListener(OPEN_GLOBAL_REMINDER_EVENT, openGlobalReminder);
    return () => window.removeEventListener(OPEN_GLOBAL_REMINDER_EVENT, openGlobalReminder);
  }, []);

  // The AppBar "+ Reminder" is the GLOBAL entry point, so Global is always
  // its default mode. The selected entity still rides along as defaultRef so
  // switching to Contact/Property mode pre-fills it.
  const reminderDefaults = { mode: "global", ref: getReminderContextDefaults(userDomain).ref };

  return (
    <header className="appbar">
      <div className="appbar__left">
        {isSmall && (
          <AppIconButton onClick={toggleSidebar}>
            <MenuIcon fontSize="small" />
          </AppIconButton>
        )}
        {location.pathname.startsWith("/addressbook") && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 0.5, minWidth: 0 }}>
              <TextField
                size="small"
                placeholder="Search contacts..."
                value={addressbookToolbar.searchQuery || ""}
                onChange={(e) => addressbookToolbar.onSearchChange?.(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addressbookToolbar.onSearchSubmit?.(); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: addressbookToolbar.searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => addressbookToolbar.onSearchChange?.("")}
                        sx={{ p: 0.25 }}
                        aria-label="Clear contact search"
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  width: { xs: 150, sm: 210, md: 260 },
                  "& .MuiOutlinedInput-root": { height: 32, fontSize: 12 },
                }}
              />
            </Box>
          </>
        )}
        {location.pathname.startsWith("/properties") && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 0.5, minWidth: 0 }}>
            <TextField
              size="small"
              placeholder="Search properties..."
              value={propertiesToolbar.searchQuery || ""}
              onChange={(e) => propertiesToolbar.onSearchChange?.(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") propertiesToolbar.onSearchSubmit?.(); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: propertiesToolbar.searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => propertiesToolbar.onSearchChange?.("")} sx={{ p: 0.25 }} aria-label="Clear property search">
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: { xs: 170, sm: 230, md: 280 }, "& .MuiOutlinedInput-root": { height: 32, fontSize: 12 } }}
            />
          </Box>
        )}
        {location.pathname.startsWith("/match") && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mr: 0.5, minWidth: 0 }}>
            <TextField
              size="small"
              placeholder="Search properties..."
              value={matchToolbar.propertySearchQuery || ""}
              onChange={(e) => matchToolbar.onPropertySearchChange?.(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") matchToolbar.onPropertySearchSubmit?.(); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: matchToolbar.propertySearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => matchToolbar.onPropertySearchChange?.("")} sx={{ p: 0.25 }} aria-label="Clear match property search">
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: { xs: 150, sm: 190, md: 230 }, "& .MuiOutlinedInput-root": { height: 32, fontSize: 12 } }}
            />
            <TextField
              size="small"
              placeholder="Search contacts..."
              value={matchToolbar.contactSearchQuery || ""}
              onChange={(e) => matchToolbar.onContactSearchChange?.(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") matchToolbar.onContactSearchSubmit?.(); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: matchToolbar.contactSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => matchToolbar.onContactSearchChange?.("")} sx={{ p: 0.25 }} aria-label="Clear match contact search">
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ width: { xs: 150, sm: 190, md: 230 }, "& .MuiOutlinedInput-root": { height: 32, fontSize: 12 } }}
            />
          </Box>
        )}
      </div>

      <div className="appbar__right">
        {location.pathname.startsWith("/addressbook") && (
          <>
            <Gate flag="panel.addressbook.contacts">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Contacts"
              icon={<ContactsIcon fontSize="small" />}
              circleColor={isPanelOpen("contacts") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("contacts")}
              title="Toggle Contacts Panel"
              aria-label="Toggle Contacts Panel"
            />
            </Gate>
            <Gate flag="panel.addressbook.details">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Details"
              icon={<InfoOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("details") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                const opening = !isPanelOpen("details");
                if (opening) {
                  setPanels({ details: true, comments: false, chat: false });
                } else {
                  setPanels({ details: false });
                }
              }}
              title="Toggle Details Panel"
              aria-label="Toggle Details Panel"
            />
            </Gate>
            <Gate flag="panel.addressbook.comments">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Comments"
              icon={<ChatBubbleOutlineIcon fontSize="small" />}
              circleColor={isPanelOpen("comments") ? "#3B82F6" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                const opening = !isPanelOpen("comments");
                if (opening) {
                  setPanels({ comments: true, details: false, chat: false });
                } else {
                  setPanels({ comments: false, details: true });
                }
              }}
              title="Toggle Comments Panel"
              aria-label="Toggle Comments Panel"
            />
            </Gate>
            <Gate flag="panel.addressbook.chat">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Chat"
              icon={<SmartToyOutlinedIcon fontSize="small" />}
              circleColor={isMobile ? "#D1D5DB" : (isPanelOpen("chat") ? "#7c3aed" : "#9CA3AF")}
              iconColor="#FFFFFF"
              labelColor={isMobile ? "#9CA3AF" : "text.secondary"}
              onClick={handleChatClick}
              title="Toggle Chat Panel"
              aria-label="Toggle Chat Panel"
            />
            </Gate>
            <Gate flag="panel.addressbook.journal">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Journal"
              icon={<MenuBookIcon fontSize="small" />}
              circleColor={isPanelOpen("journal") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("journal")}
              title="Toggle Journal Panel"
              aria-label="Toggle Journal Panel"
            />
            </Gate>
            <Gate flag="panel.addressbook.reminders">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Reminders"
              icon={remindersToggleIcon}
              circleColor={isPanelOpen("reminders") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("reminders")}
              title="Toggle Reminders Panel"
              aria-label="Toggle Reminders Panel"
            />
            </Gate>
            <Gate flag="panel.addressbook.files">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Files"
              icon={<FolderIcon fontSize="small" />}
              circleColor={isPanelOpen("files") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("files")}
              title="Toggle Files Panel"
              aria-label="Toggle Files Panel"
            />
            </Gate>
            <Gate flag="panel.addressbook.properties">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Properties"
              icon={<ApartmentIcon fontSize="small" />}
              circleColor={isPanelOpen("properties") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("properties")}
              title="Toggle Linked Properties Panel"
              aria-label="Toggle Linked Properties Panel"
            />
            </Gate>
            <Gate flag="appbar.addReminder">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Reminder"
              icon={<AddIcon fontSize="small" />}
              circleColor="#D97706"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={handleCreateGlobalReminder}
              title="Create reminder"
              aria-label="Create reminder"
            />
            </Gate>
          </>
        )}
        {location.pathname.startsWith("/properties") && (
          <>
            <Gate flag="panel.properties.contacts">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Contacts"
              icon={<ContactsIcon fontSize="small" />}
              circleColor={isPanelOpen("contacts") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("contacts")}
              title="Toggle Contacts Panel"
              aria-label="Toggle Contacts Panel"
            />
            </Gate>
            <Gate flag="panel.properties.map">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Map"
              icon={<MapOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("map") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                const opening = !isPanelOpen("map");
                if (opening) {
                  setPanels({ map: true, details: false, comments: false, chat: false });
                } else {
                  setPanels({ map: false, details: true });
                }
              }}
              title="Toggle Map Panel"
              aria-label="Toggle Map Panel"
            />
            </Gate>
            <Gate flag="panel.properties.details">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Details"
              icon={<InfoOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("details") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                const opening = !isPanelOpen("details");
                if (opening) {
                  setPanels({ details: true, comments: false, chat: false });
                } else {
                  setPanels({ details: false });
                }
              }}
              title="Toggle Details Panel"
              aria-label="Toggle Details Panel"
            />
            </Gate>
            <Gate flag="panel.properties.comments">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Comments"
              icon={<ChatBubbleOutlineIcon fontSize="small" />}
              circleColor={isPanelOpen("comments") ? "#3B82F6" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                const opening = !isPanelOpen("comments");
                if (opening) {
                  setPanels({ comments: true, details: false, chat: false });
                } else {
                  setPanels({ comments: false, details: true });
                }
              }}
              title="Toggle Comments Panel"
              aria-label="Toggle Comments Panel"
            />
            </Gate>
            <Gate flag="panel.properties.chat">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Chat"
              icon={<SmartToyOutlinedIcon fontSize="small" />}
              circleColor={isMobile ? "#D1D5DB" : (isPanelOpen("chat") ? "#7c3aed" : "#9CA3AF")}
              iconColor="#FFFFFF"
              labelColor={isMobile ? "#9CA3AF" : "text.secondary"}
              onClick={handleChatClick}
              title="Toggle Chat Panel"
              aria-label="Toggle Chat Panel"
            />
            </Gate>
            <Gate flag="panel.properties.journal">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Journal"
              icon={<MenuBookIcon fontSize="small" />}
              circleColor={isPanelOpen("journal") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("journal")}
              title="Toggle Journal Panel"
              aria-label="Toggle Journal Panel"
            />
            </Gate>
            <Gate flag="panel.properties.reminders">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Reminders"
              icon={remindersToggleIcon}
              circleColor={isPanelOpen("reminders") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("reminders")}
              title="Toggle Reminders Panel"
              aria-label="Toggle Reminders Panel"
            />
            </Gate>
            <Gate flag="panel.properties.files">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Files"
              icon={<FolderIcon fontSize="small" />}
              circleColor={isPanelOpen("files") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("files")}
              title="Toggle Files Panel"
              aria-label="Toggle Files Panel"
            />
            </Gate>
            <Gate flag="panel.properties.images">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Images"
              icon={<PhotoLibraryIcon fontSize="small" />}
              circleColor={isPanelOpen("images") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("images")}
              title="Toggle Images Panel"
              aria-label="Toggle Images Panel"
            />
            </Gate>
            <Gate flag="panel.properties.verify">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Verify"
              icon={<FactCheckIcon fontSize="small" />}
              circleColor={isPanelOpen("verify") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("verify")}
              title="Toggle Verify Panel"
              aria-label="Toggle Verify Panel"
            />
            </Gate>
            <Gate flag="appbar.addReminder">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Reminder"
              icon={<AddIcon fontSize="small" />}
              circleColor="#D97706"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={handleCreateGlobalReminder}
              title="Create reminder"
              aria-label="Create reminder"
            />
            </Gate>
          </>
        )}
        {location.pathname.startsWith("/match") && (
          <>
            <Gate flag="panel.match.contacts">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Contacts"
              icon={<ContactsIcon fontSize="small" />}
              circleColor={isPanelOpen("contacts") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("contacts")}
              title="Toggle Contacts Panel"
              aria-label="Toggle Contacts Panel"
            />
            </Gate>
            <Gate flag="panel.match.details">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Details"
              icon={<InfoOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("details") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                const opening = !isPanelOpen("details");
                if (opening) {
                  setPanels({ details: true, map: false, chat: false });
                } else {
                  setPanels({ details: false });
                }
              }}
              title="Toggle Details Panel"
              aria-label="Toggle Details Panel"
            />
            </Gate>
            <Gate flag="panel.match.map">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Map"
              icon={<MapOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("map") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                const opening = !isPanelOpen("map");
                if (opening) {
                  setPanels({ map: true, details: false, chat: false });
                } else {
                  setPanels({ map: false, details: true });
                }
              }}
              title="Toggle Map Panel"
              aria-label="Toggle Map Panel"
            />
            </Gate>
            <Gate flag="panel.match.chat">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Chat"
              icon={<SmartToyOutlinedIcon fontSize="small" />}
              circleColor={isMobile ? "#D1D5DB" : (isPanelOpen("chat") ? "#7c3aed" : "#9CA3AF")}
              iconColor="#FFFFFF"
              labelColor={isMobile ? "#9CA3AF" : "text.secondary"}
              onClick={handleChatClick}
              title="Toggle Chat Panel"
              aria-label="Toggle Chat Panel"
            />
            </Gate>
            <Gate flag="panel.match.files">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Files"
              icon={<FolderIcon fontSize="small" />}
              circleColor={isPanelOpen("files") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("files")}
              title="Toggle Files Panel"
              aria-label="Toggle Files Panel"
            />
            </Gate>
          </>
        )}
        {location.pathname.startsWith("/campaigns") && (
          <>
            <Gate flag="panel.campaigns.list">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Campaigns"
              icon={<MenuBookIcon fontSize="small" />}
              circleColor={isPanelOpen("campaignList") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("campaignList")}
              title="Toggle Campaign List Panel"
              aria-label="Toggle Campaign List Panel"
            />
            </Gate>
            <Gate flag="panel.campaigns.details">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Details"
              icon={<InfoOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("campaignDetail") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("campaignDetail")}
              title="Toggle Campaign Details Panel"
              aria-label="Toggle Campaign Details Panel"
            />
            </Gate>
            <Gate flag="panel.campaigns.contacts">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Contacts"
              icon={<ContactsIcon fontSize="small" />}
              circleColor={isPanelOpen("campaignContacts") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("campaignContacts")}
              title="Toggle Campaign Contacts Panel"
              aria-label="Toggle Campaign Contacts Panel"
            />
            </Gate>
            <Gate flag="panel.campaigns.analytics">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Analytics"
              icon={<InsightsIcon fontSize="small" />}
              circleColor={isPanelOpen("campaignAnalytics") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("campaignAnalytics")}
              title="Toggle Campaign Analytics Panel"
              aria-label="Toggle Campaign Analytics Panel"
            />
            </Gate>
          </>
        )}
        {location.pathname.startsWith("/loans") && (
          <>
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Inbox"
              icon={<MenuBookIcon fontSize="small" />}
              circleColor={isPanelOpen("loanInbox") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("loanInbox")}
              title="Toggle Loan Inbox Panel"
              aria-label="Toggle Loan Inbox Panel"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Email"
              icon={<MailOutlineIcon fontSize="small" />}
              circleColor={isPanelOpen("loanEmail") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("loanEmail")}
              title="Toggle Email Panel"
              aria-label="Toggle Email Panel"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Loan"
              icon={<FactCheckOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("loanFields") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("loanFields")}
              title="Toggle Extracted Loan Panel"
              aria-label="Toggle Extracted Loan Panel"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Files"
              icon={<FolderIcon fontSize="small" />}
              circleColor={isPanelOpen("loanFiles") ? "#D97706" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("loanFiles")}
              title="Toggle Files Panel"
              aria-label="Toggle Files Panel"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Emails"
              icon={<AllInboxIcon fontSize="small" />}
              circleColor={isPanelOpen("loanEmails") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("loanEmails")}
              title="Toggle All Emails Panel"
              aria-label="Toggle All Emails Panel"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Loans"
              icon={<TableChartOutlinedIcon fontSize="small" />}
              circleColor={isPanelOpen("loanLoans") ? "#1D4ED8" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => togglePanel("loanLoans")}
              title="Toggle All Loans Panel"
              aria-label="Toggle All Loans Panel"
            />
          </>
        )}
        {location.pathname.startsWith("/ai") && (
          <>
            <Gate flag="panel.ai.writingMode">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Writing Mode"
              icon={<AutoAwesomeIcon fontSize="small" />}
              circleColor={isPanelOpen("voiceProfiles") ? "#7c3aed" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                setPanels({
                  voiceProfiles: true,
                  textProfiles: false,
                  voiceProfileDetail: true,
                  enrichmentRules: false,
                  enrichmentRulesDetail: false,
                });
              }}
              title="Toggle Writing Mode"
              aria-label="Toggle Writing Mode"
            />
            </Gate>
            <Gate flag="panel.ai.textMode">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Text Mode"
              icon={<EditNoteIcon fontSize="small" />}
              circleColor={isPanelOpen("textProfiles") ? "#7c3aed" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                setPanels({
                  voiceProfiles: false,
                  textProfiles: true,
                  voiceProfileDetail: true,
                  enrichmentRules: false,
                  enrichmentRulesDetail: false,
                });
              }}
              title="Toggle Text Mode"
              aria-label="Toggle Text Mode"
            />
            </Gate>
            <Gate flag="panel.ai.enrichmentMode">
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
              label="Enrichment Mode"
              icon={<RuleIcon fontSize="small" />}
              circleColor={isPanelOpen("enrichmentRules") ? "#0ea5e9" : "#9CA3AF"}
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={() => {
                setPanels({
                  voiceProfiles: false,
                  textProfiles: false,
                  voiceProfileDetail: false,
                  enrichmentRules: true,
                  enrichmentRulesDetail: true,
                });
              }}
              title="Toggle Enrichment Mode"
              aria-label="Toggle Enrichment Mode"
            />
            </Gate>
          </>
        )}
        {location.pathname.startsWith("/journal") && (
          // TODO(toggles): register journal appbar actions
          <>
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Note"
              icon={<TextFieldsIcon fontSize="small" />}
              circleColor="#2563EB"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={dispatchJournalAction("note")}
              title="Add a note"
              aria-label="Add a note"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Reminder"
              icon={<NotificationsActiveIcon fontSize="small" />}
              circleColor="#D97706"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={dispatchJournalAction("reminder")}
              title="Add a reminder"
              aria-label="Add a reminder"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Photo"
              icon={<PhotoCameraIcon fontSize="small" />}
              circleColor="#7C3AED"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={dispatchJournalAction("photo")}
              title="Add a photo"
              aria-label="Add a photo"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Audio"
              icon={<GraphicEqIcon fontSize="small" />}
              circleColor="#DC2626"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={dispatchJournalAction("audio")}
              title="Record audio"
              aria-label="Record audio"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Speech"
              icon={<KeyboardVoiceIcon fontSize="small" />}
              circleColor="#2563EB"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={dispatchJournalAction("speech")}
              title="Speech to text"
              aria-label="Speech to text"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Speak"
              icon={<VolumeUpIcon fontSize="small" />}
              circleColor="#2563EB"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={dispatchJournalAction("speak")}
              title="Text to speech"
              aria-label="Text to speech"
            />
            <UniversalActionButton
              styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
              label="Link"
              icon={<LinkIcon fontSize="small" />}
              circleColor="#DB2777"
              iconColor="#FFFFFF"
              labelColor="text.secondary"
              onClick={dispatchJournalAction("link")}
              title="Add a link"
              aria-label="Add a link"
            />
          </>
        )}
        <div className="appbar__right-end">
          {/* Reminder inbox: one global list of every fired reminder, opened
              over any page without disturbing the workspace panels. */}
          <UniversalActionButton
            styleType={UNIVERSAL_BUTTON_STYLES.SQUARE}
            label="Inbox"
            icon={remindersToggleIcon}
            circleColor={reminderInboxOpen ? "#D97706" : "#9CA3AF"}
            iconColor="#FFFFFF"
            labelColor="text.secondary"
            onClick={toggleReminderInbox}
            title="Reminder inbox"
            aria-label="Reminder inbox"
          />
          <Gate flag="appbar.share">
          <UniversalActionButton
            styleType={UNIVERSAL_BUTTON_STYLES.STYLE1}
            label="Share"
            icon={<OpenInNewIcon fontSize="small" />}
            circleColor="#3B82F6"
            iconColor="#FFFFFF"
            labelColor="text.secondary"
            onClick={openContextShare}
            title="Open Share"
            aria-label="Open Share"
          />
          </Gate>
          <HelpLauncher />
        </div>
      </div>
      <ContextSharePopup
        open={contextShareOpen}
        onClose={() => setContextShareOpen(false)}
        url={CONTEXTSHARE_URL}
        launchPath={`${location.pathname}${location.search}${location.hash}`}
      />
      <ReminderCreateDialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        authorId={authorId}
        domain={userDomain}
        defaultMode={reminderDefaults.mode}
        defaultRef={reminderDefaults.ref}
      />
      <Snackbar
        open={mobileChatToastOpen}
        autoHideDuration={3600}
        onClose={() => setMobileChatToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setMobileChatToastOpen(false)} severity="info" sx={{ width: "100%" }}>
          Use your desktop for full access.
        </Alert>
      </Snackbar>
    </header>
  );
};

export default AppBar;
